/**
 * @module app.shared
 */

import { autoinject, bindingMode, BindingEngine, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Subscription } from 'aurelia-event-aggregator';
import { IToggle } from 'app/shared/elements/forms/input-base';
import { equals } from 'app/shared/lib/noop';
import { selectText } from 'app/shared/lib/dom';
import { getKey, Key } from 'app/shared/lib/keys';
import { MatcherFunction } from 'app/shared/lib/matcher';

export enum CheckboxState {
    Checked = 1,
    Unchecked,
    Indeterminate
}

const CONFIRM_KEYS = [
    Key.Enter,
    Key.Space
]

@autoinject()
export class EssCheckboxCustomElement implements IToggle {

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    checked: any = null;

    @bindable.string()
    public testName: string = '';

    @bindable.boolean()
    public required: boolean = false;

    @bindable() model: any;

    @bindable()
    public autocomplete: string = 'off';

    @bindable()
    readonly: boolean = false;

    @bindable()
    disabled: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    matcher: MatcherFunction = equals;

    @observable()
    public state = CheckboxState.Unchecked;

    @bindable.stringNull()
    public groupName: string | null = null;

    @bindable.stringNull()
    public tooltip: string | null = null;

    @bindable.stringNull()
    public labelId: string | null = null;

    @bindable() protected labelledbyId: string | null = null;  //An element's `id`. Sets the `aria-labelledby`

    protected checkedArraySubscription: Subscription | null = null;
    protected modelArraySubscription: Subscription | null = null;

    // refs
    protected checkbox!: HTMLInputElement;

    selectText = selectText;
    CheckboxState = CheckboxState;

    constructor(protected readonly bindingEngine: BindingEngine) { }

    protected attached() {
        this.syncElement();
    }

    protected checkedChanged(checked: any) {
        if(Array.isArray(checked)) {
            this.checkedArraySubscription = this.bindingEngine.collectionObserver(checked).subscribe(() => this.syncElement());
        } else if(this.checkedArraySubscription) {
            this.checkedArraySubscription.dispose();
            this.checkedArraySubscription = null;
        }
        this.syncElement();
    }

    protected handleKeydown(event: KeyboardEvent): boolean {
        if(!event.defaultPrevented && CONFIRM_KEYS.includes(getKey(event) as Key)) {
            event.preventDefault();
            this.checkbox.click();
        }
        return true;
    }

    protected modelChanged(model: any) {
        if(Array.isArray(model)) {
            this.modelArraySubscription = this.bindingEngine.collectionObserver(model).subscribe(() => this.syncElement());
        } else if(this.modelArraySubscription) {
            this.modelArraySubscription.dispose();
            this.modelArraySubscription = null;
        }
        this.syncElement();
    }

    protected stateChanged(state: CheckboxState) {
        if(this.checkbox) {
            this.checkbox.checked = (state === CheckboxState.Checked);
            this.checkbox.indeterminate = (state === CheckboxState.Indeterminate);
        }
    }

    protected getAriaChecked(state: CheckboxState): 'true' | 'false' | 'mixed' {
        switch(state) {
            case CheckboxState.Checked:
                return 'true'
            case CheckboxState.Indeterminate:
                return 'mixed'
            case CheckboxState.Unchecked:
                return 'false'
        }
    }

    // INFO [Matt] Ensures the element matches the view-model state.
    protected syncElement() {
        const { checkbox, checked, model, matcher } = this;

        if(!checkbox) {
            return;
        }

        const checkedIsArray = Array.isArray(checked);
        const modelIsArray = Array.isArray(model);

        if(checkedIsArray) {

            // if checked and model are arrays, checked if equal, unchecked if either are empty, indeterminate otherwise
            if(modelIsArray) {
                let matched = 0;
                model.forEach((m: any) => checked.forEach((c: any) => {
                    if(!!matcher(m, c)) {
                        matched += 1;
                    }
                }));

                if(matched === 0) {
                    this.state = CheckboxState.Unchecked;
                } else if(checked.length === model.length && checked.length === matched) {
                    this.state = CheckboxState.Checked;
                } else {
                    this.state = CheckboxState.Indeterminate;
                }

                // if checked is array, checked if model is in array
            } else {
                this.state = checked.some((c: any) => !!matcher(model, c))
                    ? CheckboxState.Checked
                    : CheckboxState.Unchecked;
            }
        } else {

            // if model exists, checked if checked is model
            if(model !== undefined) {
                this.state = matcher(model, checked)
                    ? CheckboxState.Checked
                    : CheckboxState.Unchecked;
            }

            // if model doesn't exist, checked if checked is truthy
            else {
                if(!!checked) {
                    this.state = CheckboxState.Checked;
                } else if(checked === null) {
                    this.state = CheckboxState.Indeterminate;
                } else {
                    this.state = CheckboxState.Unchecked;
                }
            }
        }
    }

    // INFO [Matt] Ensures the bindings match the element state.
    protected syncValues() {
        const { checkbox, checked, model, matcher } = this;
        const checkedIsArray = Array.isArray(checked);
        const modelIsArray = Array.isArray(model);

        if(checkedIsArray) {
            if(modelIsArray) {
                if(checkbox.checked) {
                    checked.splice(0, Infinity, ...model);
                } else {
                    checked.splice(0, Infinity);
                }
            } else {
                const { length } = checked;
                checked.slice().reverse().forEach((c: any, idx: number) => {
                    if(!!matcher(c, model)) {
                        checked.splice(length - idx - 1, 1);
                    }
                });
                if(checkbox.checked) {
                    checked.push(model);
                }
            }
        } else {
            if(model !== undefined) {
                this.checked = checkbox.checked ? model : null;
            }
            else {
                this.checked = this.checkbox.checked;
            }
        }
    }

}
